import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getFeaturedProducts, getPortada } from '../services/api';
import ProductCard from '../components/product/ProductCard';
import AboutSection from '../components/home/AboutSection';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const FeaturedProducts = () => {
  const { t } = useLanguage();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const data = await getFeaturedProducts();
        setFeaturedProducts(data);
      } catch (error) {
        console.error('Error loading featured products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedProducts();
  }, []);

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">{t('bestSellers')}</h2>
          <p className="text-muted-foreground text-lg">
            Los productos más populares de NAGOC GROUP
          </p>
        </div>
        
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Hero Component for Home with Portada
const HomeHero = ({ portada }) => {
  const navigate = useNavigate();

  if (!portada) return null;

  return (
    <div 
      className="relative min-h-[85vh] flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${portada.imageUrl})`,
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
          {portada.title}
        </h1>
        <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto">
          {portada.subtitle}
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Button 
            size="lg" 
            onClick={() => navigate('/catalog')}
            className="bg-white text-black hover:bg-white/90"
          >
            Ver Catálogo
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            onClick={() => navigate('/featured')}
            className="border-white text-white hover:bg-white/10"
          >
            Productos Destacados
          </Button>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [portada, setPortada] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPortada = async () => {
      try {
        const data = await getPortada('home');
        setPortada(data);
      } catch (error) {
        console.error('Error loading home portada:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortada();
  }, []);

  return (
    <div className="min-h-screen">
      {loading ? (
        <div className="min-h-[85vh] flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : (
        <HomeHero portada={portada} />
      )}
      <FeaturedProducts />
      <AboutSection />
    </div>
  );
};

export default Home;
